// liTime.js — pure time helpers for Local Intelligence. No React, no state:
// unit-testable and reusable. Everything reads the PROPERTY's wall clock from
// the fn_li_page response (as_of / as_of_label), never the browser clock.

// how many hours shift a meal's When window (see recommendedWhenFor below)

// ── Property wall clock ─────────────────────────────────────────────────────────
// work-page-fixes Item 4 — the property's own clock, not the browser's. The
// front desk may sit in any timezone, so the wall time must come from the
// fn_li_page response: `as_of` (a full ISO 8601 timestamp in the live API,
// e.g. "2026-09-30T19:19:19+00:00") or, when `as_of` is date-only (older
// captures such as the verde fixture send "2026-09-30"), the trailing clock in
// `as_of_label` ("Wednesday, Sep 30 · 7:41 AM"). The time-of-day is read from
// the STRING, never via new Date(as_of).getHours() — that would re-express the
// moment in the browser's timezone and defeat the point. The result is a
// deliberately fake-local Date: built with local-constructor fields holding
// the property's wall clock, so getHours()/getMinutes() return the property
// time in every browser. Consumers (the "Now" pill in TimeChips, the meal
// auto-select) use it exactly like a Date. Last resort when neither source
// carries a time: the browser clock (the old behavior).
export const clockFromIsoText = (raw) => {
  const m = /T(\d{2}):(\d{2})/.exec(String(raw || ''));
  return m ? { h: Number(m[1]), m: Number(m[2]) } : null;
};

export const clockFromAsOfLabel = (label) => {
  const m = /(\d{1,2}):(\d{2})\s*([AP])M/i.exec(String(label || ''));
  if (!m) return null;
  let h = Number(m[1]) % 12;
  if (m[3].toUpperCase() === 'P') h += 12;
  return { h, m: Number(m[2]) };
};

export const propertyTimeOf = (asOfRaw, asOfLabel) => {
  const clock = clockFromIsoText(asOfRaw) || clockFromAsOfLabel(asOfLabel);
  if (!clock) return new Date();
  return new Date(2000, 0, 1, clock.h, clock.m, 0);
};

// ── Meal auto-select ────────────────────────────────────────────────────────────
// Item 1 — pick the meal need tile that matches the current time of day so the
// combined panel opens with results already showing. Hours 5–10 → breakfast,
// 11–16 → lunch, 17–4 (overnight wrap) → dinner. Keys match need_tiles[].need_key
// from the fn_li_page response (confirmed: "breakfast", "lunch", "dinner").
export const mealNeedKeyForHour = (hour) => {
  if (hour >= 5 && hour <= 10) return 'breakfast';
  if (hour >= 11 && hour <= 16) return 'lunch';
  return 'dinner';
};

// ── When-window guardrail ───────────────────────────────────────────────────────
// work-page-fixes Item 4 — soft guardrail: each meal has a canonical When
// window for today AND for tomorrow, and a (meal, When) pair that misses both
// shifts to the fitting option (labels match the page's time_options,
// including the two synthesized lunch slots, "Today 12 PM" and "Tomorrow 12
// PM"). Canonical windows, keyed by the property's hour from as_of
// (propertyTimeOf): Breakfast → "Now" in the morning (5–10), else "Tomorrow 8
// AM"; Lunch → "Today 12 PM" before noon (hours 0–10), "Now" from lunchtime
// on; Dinner → "Tonight 7 PM" — per the desk's request, clicking Dinner always
// lands on the tonight window. A tomorrow pick always STAYS tomorrow,
// remapped to the meal's own tomorrow slot (Breakfast → "Tomorrow 8 AM",
// Lunch → "Tomorrow 12 PM", Dinner → "Tomorrow 7 PM") — the guest asked about
// tomorrow, so only the slot changes, never the day. Pairs that already fit
// return null. Callers map the returned label back to a time_options index;
// an unknown label means no shift.
export const recommendedWhenFor = (needKey, whenLabel, propertyHour) => {
  if (!needKey || !whenLabel) return null;
  const morning = propertyHour >= 5 && propertyHour <= 10;
  const beforeNoon = propertyHour >= 0 && propertyHour <= 10;
  if (/^Tomorrow/.test(whenLabel)) {
    const slot = { breakfast: 'Tomorrow 8 AM', lunch: 'Tomorrow 12 PM', dinner: 'Tomorrow 7 PM' }[needKey];
    return slot && slot !== whenLabel ? slot : null;
  }
  if (needKey === 'breakfast') {
    if (whenLabel === 'Now') return morning ? null : 'Tomorrow 8 AM';
    return morning ? 'Now' : 'Tomorrow 8 AM'; // "Today 12 PM", "Tonight 7 PM"
  }
  if (needKey === 'lunch') {
    if (whenLabel === 'Now') return beforeNoon ? 'Today 12 PM' : null;
    if (whenLabel === 'Today 12 PM') return null;
    return beforeNoon ? 'Today 12 PM' : 'Now'; // "Tonight 7 PM"
  }
  if (needKey === 'dinner') {
    return whenLabel === 'Tonight 7 PM' ? null : 'Tonight 7 PM'; // "Now", "Today 12 PM"
  }
  return null;
};

// ── Synthesized lunch slots ─────────────────────────────────────────────────────
// work-page-fixes Item 4 — the database's time_options carry no lunch window
// (Now, Tonight 7 PM, Tomorrow 8 AM, Tomorrow 7 PM), so the page
// synthesizes the two missing slots of the today/tomorrow meal grid:
// "Today 12 PM" right after "Now" and "Tomorrow 12 PM" right after
// "Tomorrow 8 AM". Each `at` is the property's as_of date (or date + 1) at
// 12:00:00 with as_of's own timezone suffix (built from asOfIso's text,
// never the browser clock); when as_of is date-only (older captures) the
// synthesized option falls back to a neighbouring database stamp ("Now"
// for today, "Tomorrow 8 AM" for tomorrow). Returns a NEW array — the caller's
// list is never mutated. The augmented list is the single source the rendered
// pills and the page's timeIndex math (runAsk, resolveWhenForNeed) share, so
// the rendered index and the sent index can never drift.
export const augmentTimeOptions = (rawList, asOfIso) => {
  const list = Array.isArray(rawList) ? [...rawList] : [];
  if (list.length === 0) return list;
  const has = (re) => list.some((o) => re.test(String(o.label || '')));
  // The property-local noon stamp `plusDays` from as_of's date, keeping
  // as_of's timezone suffix (the codebase reads as_of's clock text as the
  // property's wall time — see propertyTimeOf — so its date part is the
  // property's calendar date too).
  const atNoon = (plusDays) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(asOfIso || '');
    if (!m) return null;
    const day = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + plusDays)).toISOString().slice(0, 10);
    const off = /([+-]\d{2}:\d{2}|Z)\s*$/.exec(asOfIso);
    return off ? `${day}T12:00:00${off[1] === 'Z' ? '+00:00' : off[1]}` : `${day}T12:00:00`;
  };
  const insertAfter = (anchorLabel, opt) => {
    const i = list.findIndex((o) => o.label === anchorLabel);
    list.splice(i === -1 ? list.length : i + 1, 0, opt);
  };
  if (!has(/today\s*12\s*pm|lunch|noon/i)) {
    insertAfter('Now', { at: atNoon(0) ?? list.find((o) => o.label === 'Now')?.at ?? null, label: 'Today 12 PM' });
  }
  if (!has(/tomorrow\s*12\s*pm/i)) {
    insertAfter('Tomorrow 8 AM', { at: atNoon(1) ?? list.find((o) => o.label === 'Tomorrow 8 AM')?.at ?? null, label: 'Tomorrow 12 PM' });
  }
  return list;
};
